import { forwardRef, useState, useEffect, type HTMLAttributes } from 'react'
import { twMerge } from 'tailwind-merge'
import { CardFrame } from '@/components/base/CardFrame'
import { IconBadge } from '@/components/base/IconBadge'
import { BackgroundImage } from '@/components/base/BackgroundImage'
import { RetentionHeader } from '@/components/composite/RetentionHeader'
import { ShieldIcon, FlashIcon, AnglesIcon } from '@/icons'
import { levelPanelVariants } from './LevelPanel.variants'

export interface LevelPanelTranslations {
  /** Header title */
  title?: string
  /** Header subtitle */
  subtitle?: string
  /** Header tooltip */
  tooltip?: string
  /** "Your level:" label */
  yourLevel?: string
  /** "MAX LEVEL" text */
  maxLevel?: string
  /** "Multiplier boost:" label */
  multiplierBoost?: string
  /** "Level X" text for next level (will have level number appended) */
  level?: string
}

export interface LevelPanelProps extends HTMLAttributes<HTMLDivElement> {
  /** Current player level (0-10) */
  level?: number
  /** Current XP (unitless, based on total wagered) */
  currentXP?: number
  /** XP needed for next level */
  nextLevelXP?: number
  /** Current multiplier boost percentage */
  multiplierBonus?: number
  /** Next level's multiplier boost percentage */
  nextLevelBonus?: number
  /** Whether max level has been reached */
  isMaxLevel?: boolean
  /** Background image URL */
  bgImageUrl?: string
  /** Enable mount animations */
  animate?: boolean
  /** Translation strings */
  translations?: LevelPanelTranslations
}

// More precise XP formatting - show extra decimals for large numbers so small bets are visible
const formatXP = (xp: number): string => {
  if (xp >= 1_000_000) {
    // For millions, show 2 decimal places (e.g., 1.72M) so $10 bets are visible
    return `${(xp / 1_000_000).toFixed(2)}M XP`
  }
  if (xp >= 1_000) {
    // For thousands, show 1 decimal place
    return `${(xp / 1_000).toFixed(1)}K XP`
  }
  return `${xp.toLocaleString()} XP`
}

export const LevelPanel = forwardRef<HTMLDivElement, LevelPanelProps>(
  (
    {
      className,
      level = 0,
      currentXP = 0,
      nextLevelXP = 500,
      multiplierBonus = 0,
      nextLevelBonus = 0.1,
      isMaxLevel = false,
      bgImageUrl,
      animate = true,
      translations,
      ...props
    },
    ref
  ) => {
    const accentColor = 'blue'

    // Use translations with fallbacks
    const t = {
      title: translations?.title ?? 'Level Boost',
      subtitle: translations?.subtitle ?? 'Wager to earn XP and unlock multiplier boosts',
      tooltip:
        translations?.tooltip ??
        'Earn XP by wagering. Higher levels unlock permanent multiplier boosts!',
      yourLevel: translations?.yourLevel ?? 'Your level:',
      maxLevel: translations?.maxLevel ?? 'MAX LEVEL',
      multiplierBoost: translations?.multiplierBoost ?? 'Multiplier boost:',
      level: translations?.level ?? 'Level'
    }

    // Animation states
    const [animatedProgress, setAnimatedProgress] = useState(0)
    const [displayLevel, setDisplayLevel] = useState(0)
    const [showGlow, setShowGlow] = useState(false)

    // Calculate progress percentage
    const progressPercent = isMaxLevel ? 100 : Math.min((currentXP / nextLevelXP) * 100, 100)

    // Animate progress bar on mount
    useEffect(() => {
      if (animate) {
        // Start from 0 and animate to target
        setAnimatedProgress(0)
        const timer = setTimeout(() => {
          setAnimatedProgress(progressPercent)
        }, 100)
        return () => clearTimeout(timer)
      } else {
        setAnimatedProgress(progressPercent)
      }
    }, [animate, progressPercent])

    // Countup effect for level
    useEffect(() => {
      if (animate && level > 0) {
        setDisplayLevel(0)
        let current = 0
        const increment = Math.max(1, Math.floor(level / 10))
        const timer = setInterval(() => {
          current += increment
          if (current >= level) {
            setDisplayLevel(level)
            clearInterval(timer)
          } else {
            setDisplayLevel(current)
          }
        }, 50)
        return () => clearInterval(timer)
      } else {
        setDisplayLevel(level)
      }
    }, [animate, level])

    // Glow effect on multiplier
    useEffect(() => {
      if (animate && multiplierBonus > 0) {
        setShowGlow(true)
        const timer = setTimeout(() => setShowGlow(false), 1500)
        return () => clearTimeout(timer)
      }
    }, [animate, multiplierBonus])

    const valueText = isMaxLevel ? (
      <span className="text-primary font-bold">{t.maxLevel}</span>
    ) : (
      <>
        <span className="text-primary font-bold">{formatXP(currentXP)}</span>
        <span className="text-white/60"> / {formatXP(nextLevelXP)}</span>
      </>
    )

    const labelText = (
      <span className="text-white">
        {t.yourLevel}{' '}
        <strong className="text-blue transition-all duration-300">{displayLevel}</strong>
      </span>
    )

    return (
      <div className={twMerge(levelPanelVariants({ accentColor }), className)} ref={ref} {...props}>
        {bgImageUrl ? (
          <BackgroundImage
            className="
              opacity-30 h-1/2 rounded-xl
              [-webkit-mask-image:linear-gradient(to_bottom,black_50%,transparent_100%)]
              [mask-image:linear-gradient(to_bottom,black_50%,transparent_100%)]
            "
            imageSrc={bgImageUrl}
          />
        ) : null}
        <div className="absolute inset-0 bg-fade-to-transparent z-10 rounded-xl" />

        <div className="relative z-20 p-2">
          <RetentionHeader
            accentColor={accentColor}
            icon={<ShieldIcon className="w-[22px] h-[22px]" />}
            subtitle={t.subtitle}
            title={t.title}
            tooltip={t.tooltip}
          />

          <CardFrame className="mt-4 p-1">
            <CardFrame
              accentColor={accentColor}
              className="p-2 rounded-b-none bg-glass-overlay-light"
            >
              {/* Custom progress bar with animation */}
              <div className="flex flex-col gap-1">
                <div className="relative w-full h-2 rounded-full bg-white/20 overflow-hidden">
                  {/* Progress fill with gradient */}
                  <div
                    className="absolute inset-y-0 left-0 rounded-full transition-all duration-700 ease-out"
                    style={{
                      width: `${animatedProgress}%`,
                      background: 'linear-gradient(90deg, #3b82f6 0%, #06b6d4 50%, #3b82f6 100%)',
                      backgroundSize: '200% 100%',
                      animation: 'gradientShift 4s ease infinite'
                    }}
                  />
                  {/* Bright shine sweep */}
                  <div
                    className="absolute inset-y-0 left-0 rounded-full overflow-hidden pointer-events-none"
                    style={{ width: `${animatedProgress}%` }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: '-100%',
                        width: '100%',
                        height: '100%',
                        background:
                          'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.8) 50%, transparent 100%)',
                        animation: 'shine 3s ease-in-out infinite'
                      }}
                    />
                  </div>
                </div>
                <div className="flex justify-between text-base">
                  <span>{labelText}</span>
                  <span>{valueText}</span>
                </div>
                {/* Inline keyframes */}
                <style>{`
                  @keyframes shine {
                    0% { left: -100%; }
                    50%, 100% { left: 100%; }
                  }
                  @keyframes gradientShift {
                    0%, 100% { background-position: 0% 50%; }
                    50% { background-position: 100% 50%; }
                  }
                `}</style>
              </div>
            </CardFrame>
            <div className="rounded-b-xl p-2 bg-white/10 font-bold text-white text-base flex justify-between items-center border-t border-white/25">
              <div className="flex gap-2 items-center">
                <IconBadge
                  accentColor="none"
                  className={twMerge(
                    'text-dark-yellow bg-none border border-white/20 transition-all duration-500',
                    showGlow && 'shadow-[0_0_12px_rgba(255,200,0,0.6)] scale-110'
                  )}
                >
                  <FlashIcon />
                </IconBadge>

                <p>
                  {t.multiplierBoost}
                  <br />
                  <strong
                    className={twMerge(
                      'text-dark-yellow text-lg transition-all duration-500',
                      showGlow && 'text-shadow-glow scale-105'
                    )}
                  >
                    +{multiplierBonus}%
                  </strong>
                </p>
              </div>

              {!isMaxLevel && (
                <div className="flex gap-[26px] items-center text-white/60 text-base">
                  <AnglesIcon className="w-[24px] text-dark-yellow opacity-40" />

                  <p>
                    {t.level} {level + 1}
                    <br />
                    <strong className="text-lg">+{nextLevelBonus}%</strong>
                  </p>
                </div>
              )}
            </div>
          </CardFrame>
        </div>
      </div>
    )
  }
)

LevelPanel.displayName = 'LevelPanel'
